import { ReactNode } from 'react';
import { useConfig } from '@/hooks/useConfig/useConfig';
import { useDevice } from '@/hooks/useDevice/useDevice';
import { usePresentation } from '@/hooks/usePresentation/usePresentation';
import { cn } from '@/utils/cn';
import { FlexContainer } from '@cof/omni-react';
import styled from '@emotion/styled';

const MessageScreenTitle = styled('h3')<{
  fontSize: number;
  isMobile: boolean;
  edgePaddingPx: number;
}>`
  font-size: ${(props) => props.fontSize}px;
  font-weight: 600;
  padding: 0 ${({ edgePaddingPx }) => edgePaddingPx}px;
  align-self: ${({ isMobile }) => (isMobile ? 'flex-start' : '')};
`;

const MessageScreenHeader = styled('header')<{
  edgePaddingPx: number;
  blockPaddingPx: number;
}>`
  width: 100vw;
  padding: ${({ blockPaddingPx }) => blockPaddingPx}px
    ${({ edgePaddingPx }) => edgePaddingPx}px;
  display: flex;
  justify-content: center;
`;

const MessageScreenBody = styled('div')<{
  isMobile: boolean;
  edgePaddingPx: number;
  blockPaddingPx: number;
}>`
  white-space: pre-line;
  padding: 0 ${({ edgePaddingPx }) => edgePaddingPx}px
    ${({ blockPaddingPx }) => blockPaddingPx}px;
  align-self: ${({ isMobile }) => (isMobile ? 'flex-start' : '')};
`;

export const MessageScreen = ({
  iconPath,
  iconSize,
  title,
  titleSize,
  body,
  button,
  isButtonAtBottom = false,
}: {
  iconPath?: string;
  iconSize?: number;
  title: string;
  titleSize: number;
  body: ReactNode;
  button?: ReactNode;
  isButtonAtBottom?: boolean;
}) => {
  const { content, theme } = useConfig();
  const { isMobile } = useDevice();
  const { tokens } = usePresentation();

  return (
    <FlexContainer
      data-testid="MessageScreen"
      className={cn('bg-page')}
      direction="column"
      minHeight={window.innerHeight}
      alignItems="center"
      justifyContent={isButtonAtBottom ? 'space-between' : 'flex-start'}
      padding={tokens.screenPadding}
    >
      <FlexContainer direction="column" alignItems="center">
        {tokens.showLogo && (
          <MessageScreenHeader
            data-testid="MessageScreen--Header"
            edgePaddingPx={tokens.edgePaddingPx}
            blockPaddingPx={tokens.blockPaddingPx}
          >
            <img
              src={`${content.config.urlBase}${theme.logo.img.src}`}
              width={theme.logo.img.width}
              alt="logo"
              role="presentation"
            />
          </MessageScreenHeader>
        )}
        {iconPath && (
          <img src={iconPath} width={iconSize} alt="icon" role="presentation" />
        )}
        <MessageScreenTitle
          fontSize={titleSize}
          className={cn('text-neutral-primary')}
          isMobile={isMobile}
          edgePaddingPx={tokens.edgePaddingPx}
        >
          {title}
        </MessageScreenTitle>
        <MessageScreenBody
          className={cn('text-neutral-primary')}
          isMobile={isMobile}
          edgePaddingPx={tokens.edgePaddingPx}
          blockPaddingPx={tokens.blockPaddingPx}
        >
          {body}
        </MessageScreenBody>
        {!isButtonAtBottom && button}
      </FlexContainer>
      {isButtonAtBottom && button}
    </FlexContainer>
  );
};
